import { useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { PaperAirplaneIcon, PlusIcon } from '@heroicons/react/24/outline';

import IconButton from './IconButton';
import Text from './Text';
import { t } from '../i18n/langue';

/**
 * La barre de saisie — #16, variante A.
 *
 * **Une seule ligne**, les outils sous un « + ». Pas d'icônes permanentes : la
 * variante B en avait trois visibles en deux rangées, et #16 a retenu la forme
 * dépouillée.
 *
 * Texte simple sur tous les canaux. L'e-mail part avec sa **signature**, mais sans
 * pièce jointe ni destinataires supplémentaires — tout ça est explicitement hors
 * lot 1.
 *
 * **L'objet est en revanche là, et obligatoire.** `MessageController.php:619` refuse
 * un e-mail sans sujet — relevé en envoyant réellement, pas en lisant. Ce n'est
 * donc pas une « option avancée » qu'on pourrait écarter : sans lui le canal
 * e-mail ne fonctionne pas du tout. Il tient sur une ligne, pré-rempli en « Re: »
 * du dernier objet du fil, et reste modifiable.
 *
 * **Le bouton d'envoi ne se grise jamais après un échec** (#69) : un échec réseau
 * laisse le brouillon en place et le bouton actif. Il n'est indisponible que
 * pendant l'envoi en cours et sur un brouillon vide — deux états qui se résolvent
 * d'eux-mêmes.
 */
interface BarreSaisieProps {
    valeur: string;
    onChange: (valeur: string) => void;
    /** Rendu seulement sur le canal e-mail, où le serveur l'exige. */
    objet?: { valeur: string; onChange: (valeur: string) => void };
    onEnvoyer: () => void;
    /** Absent : pas de bouton « Outils de rédaction » (écran Support, sans modèles ni pièces jointes). */
    onOuvrirOutils?: () => void;
    /** Vrai pendant que l'envoi est en vol. */
    enVol: boolean;
    /** Message d'erreur du serveur, affiché tel quel — Meta répond en anglais. */
    erreur?: string | null;
    /** Champ à atteindre de l'extérieur (focus rendu après une feuille). */
    refChamp?: RefObject<HTMLTextAreaElement>;
    /**
     * Libellés propres à l'écran : le socle n'en fabrique pas d'autres que ceux de la
     * messagerie (défauts). Le Support s'en sert pour la note interne.
     */
    libelles?: { champ?: string; placeholder?: string; envoyer?: string; outils?: string };
    /** Note interne de l'équipe : fond d'avertissement, rien ne part chez le client. */
    note?: boolean;
}

/** Plancher = `min-h-touch` (#34) ; plafond ≈ quatre lignes, au-delà le champ défile. */
const HAUTEUR_MIN = 44;
const HAUTEUR_MAX = 112;

export default function BarreSaisie({
    valeur,
    onChange,
    objet,
    onEnvoyer,
    onOuvrirOutils,
    enVol,
    erreur,
    refChamp,
    libelles,
    note = false,
}: BarreSaisieProps) {
    const refInterne = useRef<HTMLTextAreaElement>(null);
    const refTexte = refChamp ?? refInterne;

    /*
     * La hauteur suit le CONTENU, qu'il vienne du clavier ou de l'extérieur : brouillon
     * vidé après envoi, brouillon restauré au retour dans le fil (audit 2026-09-23).
     *
     * Vide compris : en grande taille de texte, une seule ligne dépasse 40 px, et un
     * 40 px forcé coupait le placeholder. On mesure donc toujours, avec pour plancher
     * la zone tactile de `min-h-touch` (44 px) et pour plafond 112 px.
     *
     * Une seule source de vérité : le style posé ici, en pixels. Plus de `style`
     * dans le JSX ni d'état React — les deux se contredisaient le temps d'un rendu.
     * `height: auto` pour mesurer, puis la hauteur en pixels aussitôt, sans quoi le
     * champ resterait en `auto`, écrasé à une ligne.
     */
    useLayoutEffect(() => {
        const el = refTexte.current;
        if (!el) return;
        el.style.height = 'auto';
        // `border-box` (Tailwind) : la hauteur posée inclut la bordure, que
        // `scrollHeight` ne compte pas. Sans elle, la dernière ligne est rognée.
        const bordure = el.offsetHeight - el.clientHeight;
        el.style.height = `${Math.min(HAUTEUR_MAX, Math.max(HAUTEUR_MIN, el.scrollHeight + bordure))}px`;
    }, [valeur]);

    return (
        <div className={`border-t border-border-color-light ${note ? 'bg-warning-color/15' : 'bg-white'}`}>
            {erreur && (
                <div role="alert">
                    <Text variante="petit" ton="danger" className="px-4 pt-2">
                        {erreur}
                    </Text>
                </div>
            )}

            {objet && (
                <div className="px-3 pt-2">
                    <input
                        type="text"
                        value={objet.valeur}
                        onChange={(e) => objet.onChange(e.target.value)}
                        placeholder={t('composeur.barre.objet')}
                        aria-label={t('composeur.barre.objetEmail')}
                        className="min-h-touch w-full rounded-xl border border-border-color-light bg-main-bg-color px-3 text-base text-text-color outline-none focus:border-main-color"
                    />
                </div>
            )}

            <div className="flex items-end gap-1 px-2 py-2">
                {onOuvrirOutils && (
                    <IconButton libelle={libelles?.outils ?? t('composeur.outils.titre')} onPress={onOuvrirOutils}>
                        <PlusIcon className="h-5 w-5 text-text-color-light" />
                    </IconButton>
                )}

                <textarea
                    ref={refTexte}
                    rows={1}
                    value={valeur}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={libelles?.placeholder ?? t('composeur.barre.placeholder')}
                    aria-label={libelles?.champ ?? t('composeur.barre.champ')}
                    className={`min-h-touch flex-1 resize-none rounded-2xl border px-3 py-2 text-base text-text-color outline-none ${
                        note ? 'border-warning-color bg-white focus:border-warning-color' : 'border-border-color-light bg-main-bg-color focus:border-main-color'
                    }`}
                />

                <IconButton
                    libelle={libelles?.envoyer ?? t('composeur.barre.envoyer')}
                    onPress={onEnvoyer}
                    disabled={enVol || valeur.trim() === ''}
                >
                    <PaperAirplaneIcon className="h-5 w-5 text-main-color" />
                </IconButton>
            </div>
        </div>
    );
}
